import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  CheckCircle2,
  Circle,
  Lightbulb,
  Sparkles,
  Target,
  XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/cn";

interface Question {
  id: string;
  sub_type: string;
  topic: string;
  difficulty: 1 | 2 | 3 | 4;
  stem: string;
  passage?: string;
  choices: { label: "A" | "B" | "C" | "D" | "E"; text: string }[];
  correct: "A" | "B" | "C" | "D" | "E";
  explanation: string;
  hints: [string, string, string];
}

// Hand-authored placeholder; in production these come from
// api.practiceQuestion() and ultimately the verbal-/quant-/di-generator adapters.
const DEMO: Record<"quant" | "verbal" | "di", Question> = {
  quant: {
    id: "q-1",
    sub_type: "Problem Solving",
    topic: "Rates & Work",
    difficulty: 3,
    stem: "Two pipes fill a tank together in 6 hours. Alone, the larger pipe is 9 hours faster than the smaller. How long does the smaller pipe take alone?",
    choices: [
      { label: "A", text: "12 hours" },
      { label: "B", text: "15 hours" },
      { label: "C", text: "18 hours" },
      { label: "D", text: "20 hours" },
      { label: "E", text: "24 hours" },
    ],
    correct: "C",
    explanation:
      "Let the smaller pipe take s hours; the larger takes s − 9. Combined: 1/s + 1/(s−9) = 1/6. Multiply out: 6(2s − 9) = s(s − 9) → s² − 21s + 54 = 0 → s = 18 or 3. Reject 3 (s − 9 would be negative), so the smaller pipe alone takes 18 hours.",
    hints: [
      "Define the rates: smaller pipe = 1/s per hour.",
      "Set 1/s + 1/(s − 9) = 1/6 — the combined rate.",
      "Multiply through and solve the quadratic. Discard the negative root.",
    ],
  },
  verbal: {
    id: "v-1",
    sub_type: "Critical Reasoning",
    topic: "Strengthen",
    difficulty: 3,
    stem: "Which of the following, if true, most strengthens the argument that the new packaging caused the 12% rise in private-label sales?",
    passage:
      "A regional grocery chain switched its private-label packaging from matte to glossy. In the three months following, private-label sales rose 12%. The marketing director concluded the packaging change caused the increase and plans to roll the design out to all 240 stores.",
    choices: [
      { label: "A", text: "Private-label products are priced 20% below name brands." },
      { label: "B", text: "Total grocery sales at the chain were flat over the period." },
      {
        label: "C",
        text: "A control group of stores that kept matte packaging saw no change in private-label sales during the same period.",
      },
      { label: "D", text: "Name-brand sales at the chain decreased by 5%." },
      { label: "E", text: "The chain spent more on marketing during the period." },
    ],
    correct: "C",
    explanation:
      "We need to isolate packaging as the cause. (C) is a controlled comparison: stores that kept matte saw no change — that rules out time-based confounders. (E) introduces a confounding cause and weakens the argument.",
    hints: [
      "The conclusion is a causal claim. What kind of evidence isolates a cause?",
      "Look for a comparison that holds everything constant except packaging.",
      "Find the answer that names a control group.",
    ],
  },
  di: {
    id: "d-1",
    sub_type: "Data Sufficiency",
    topic: "Two-variable",
    difficulty: 3,
    stem: "Is the revenue R = p × x increasing? p is the average sale price and x is units sold.",
    choices: [
      { label: "A", text: "(1) alone is sufficient" },
      { label: "B", text: "(2) alone is sufficient" },
      { label: "C", text: "Both together are sufficient" },
      { label: "D", text: "Either alone is sufficient" },
      { label: "E", text: "Neither, even together, is sufficient" },
    ],
    correct: "C",
    explanation:
      "Statement (1) tells us x is decreasing. Statement (2) tells us p is increasing faster than x is decreasing. Together this means dR/dt > 0. Either alone is insufficient.",
    hints: [
      "Write R as a function. What does it mean for R to be increasing?",
      "(1) gives you only x's direction. Insufficient on its own.",
      "Combine: a stronger positive force on p than the negative force on x → R rises.",
    ],
  },
};

const TABS = [
  { key: "quant", label: "Quant" },
  { key: "verbal", label: "Verbal" },
  { key: "di", label: "Data Insights" },
] as const;

export function Practice() {
  return (
    <div className="container space-y-8 py-10">
      <div className="flex flex-col items-start justify-between gap-3 md:flex-row md:items-end">
        <div>
          <div className="flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-muted-foreground">
            <span className="hero-rule" /> Practice
          </div>
          <h1 className="mt-3 font-serif text-5xl font-normal leading-[0.98] tracking-tight md:text-6xl">
            Sharpen, <span className="italic text-primary">one at a time</span>.
          </h1>
          <p className="mt-3 text-muted-foreground">
            Adaptive question bank · difficulty calibrates as you go.
          </p>
        </div>
        <Badge variant="secondary" className="rounded-full">
          <Sparkles className="size-3" /> 12 answered today
        </Badge>
      </div>

      <Tabs defaultValue="quant" className="space-y-6">
        <TabsList className="w-full md:w-auto">
          {TABS.map((t) => (
            <TabsTrigger key={t.key} value={t.key} className="flex-1 md:flex-none">
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {TABS.map((t) => (
          <TabsContent key={t.key} value={t.key}>
            <QuestionPanel question={DEMO[t.key]} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function QuestionPanel({ question }: { question: Question }) {
  const [selected, setSelected] = useState<string | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [hintLevel, setHintLevel] = useState(0);

  const correct = selected === question.correct;

  return (
    <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
      <Card className="border-border/60 bg-card/60">
        <CardContent className="space-y-5 p-6">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Badge variant="outline">{question.sub_type}</Badge>
              <h2 className="font-display text-xl font-semibold">
                {question.topic}
              </h2>
            </div>
            <DifficultyDots level={question.difficulty} />
          </div>

          {question.passage && (
            <p className="rounded-xl border border-border/60 bg-background/40 p-5 text-sm leading-relaxed">
              {question.passage}
            </p>
          )}

          <p className="text-base">{question.stem}</p>

          <div className="space-y-2">
            {question.choices.map((c) => {
              const chosen = selected === c.label;
              const isCorrect = revealed && c.label === question.correct;
              const isWrong = revealed && chosen && !isCorrect;
              return (
                <button
                  key={c.label}
                  type="button"
                  disabled={revealed}
                  onClick={() => setSelected(c.label)}
                  className={cn(
                    "flex w-full items-center gap-3 rounded-xl border border-border/60 bg-background/40 px-4 py-3 text-left text-sm transition-all",
                    chosen && !revealed && "border-primary bg-primary/10",
                    isCorrect && "border-success bg-success/10",
                    isWrong && "border-destructive bg-destructive/10",
                  )}
                >
                  <span
                    className={cn(
                      "grid size-7 shrink-0 place-items-center rounded-full border text-xs font-bold",
                      chosen
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border",
                      isCorrect && "border-success bg-success text-success-foreground",
                      isWrong && "border-destructive bg-destructive text-destructive-foreground",
                    )}
                  >
                    {c.label}
                  </span>
                  <span className="flex-1">{c.text}</span>
                  {isCorrect && <CheckCircle2 className="size-5 text-success" />}
                  {isWrong && <XCircle className="size-5 text-destructive" />}
                </button>
              );
            })}
          </div>

          <div className="flex items-center justify-between border-t border-border/60 pt-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setHintLevel((l) => Math.min(3, l + 1))}
              disabled={hintLevel >= 3}
            >
              <Lightbulb className="size-4" />
              {hintLevel === 0 ? "Get a hint" : `Hint ${hintLevel} / 3`}
            </Button>
            <Button
              variant="default"
              size="lg"
              onClick={() => setRevealed(true)}
              disabled={!selected || revealed}
            >
              {revealed ? "Answered" : "Submit"}
              {!revealed && <ArrowRight className="size-4" />}
            </Button>
          </div>
        </CardContent>
      </Card>

      <aside className="space-y-4">
        <AnimatePresence>
          {hintLevel > 0 && !revealed && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
            >
              <Card className="border-accent/40 bg-accent/5">
                <CardContent className="space-y-3 p-5">
                  <Badge variant="accent">
                    <Lightbulb className="size-3" /> hint-generator
                  </Badge>
                  <div className="space-y-2 text-sm">
                    {question.hints.slice(0, hintLevel).map((h, i) => (
                      <p key={i}>
                        <span className="font-mono text-xs text-muted-foreground">
                          {i + 1}.
                        </span>{" "}
                        {h}
                      </p>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {revealed && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card
                className={cn(
                  "border-border/60",
                  correct
                    ? "bg-success/10 border-success/40"
                    : "bg-destructive/10 border-destructive/40",
                )}
              >
                <CardContent className="space-y-3 p-5">
                  <Badge variant={correct ? "success" : "destructive"}>
                    {correct ? "Correct" : `Answer: ${question.correct}`}
                  </Badge>
                  <p className="text-sm leading-relaxed">
                    {question.explanation}
                  </p>
                  <Badge variant="outline" className="font-mono text-[10px]">
                    explainer + answer-verifier
                  </Badge>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        <Card className="border-border/60 bg-card/60">
          <CardContent className="space-y-3 p-5">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2 font-medium">
                <Target className="size-4" /> Session accuracy
              </span>
              <span className="font-mono text-success">73%</span>
            </div>
            <Progress value={73} />
            <div className="grid grid-cols-3 gap-1 pt-1">
              {Array.from({ length: 12 }).map((_, i) => (
                <DotResult key={i} correct={i % 3 !== 1} />
              ))}
            </div>
          </CardContent>
        </Card>
      </aside>
    </div>
  );
}

function DifficultyDots({ level }: { level: 1 | 2 | 3 | 4 }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4].map((i) =>
        i <= level ? (
          <div key={i} className="size-2 rounded-full bg-primary" />
        ) : (
          <Circle key={i} className="size-2 text-muted-foreground/40" />
        ),
      )}
    </div>
  );
}

function DotResult({ correct }: { correct: boolean }) {
  return (
    <div
      className={cn(
        "h-2 rounded-full",
        correct ? "bg-success/70" : "bg-destructive/60",
      )}
    />
  );
}
